import { v4 as uuidv4 } from "uuid";

export const CATALOG_STORAGE_KEY = "productCatalog_v1";

/** Shown at the bottom of the catalogue for customers; editable in Branding. */
export const DEFAULT_FOOTNOTE_DISCLAIMER =
  "Prices, specifications, and promotional offers in this catalogue are subject to change without prior notice.";
const MAX_LOGO_BYTES = 400 * 1024;

export function createDefaultCatalog() {
  const cModel = "c_model";
  const cAh = "c_ah";
  const cWarranty = "c_warranty";
  const cLoader = "c_loader";
  const cBackup = "c_backup";
  const cWeight = "c_weight";
  const cDp = "c_dp";
  const cMrp = "c_mrp";

  const columns = [
    { id: cModel, label: "Model" },
    { id: cAh, label: "AH" },
    { id: cWarranty, label: "Warranty" },
    { id: cLoader, label: "Loader AH" },
    { id: cBackup, label: "Backup" },
    { id: cWeight, label: "Weight (Kg)" },
    { id: cDp, label: "DP" },
    { id: cMrp, label: "MRP" },
  ];

  const row = (cells) => ({
    id: uuidv4(),
    cells,
  });

  const rows = [
    row({
      [cModel]: "NS40",
      [cAh]: "40",
      [cWarranty]: "12M",
      [cLoader]: "6–8 AH",
      [cBackup]: "50 M",
      [cWeight]: "8–9",
      [cDp]: "1450",
      [cMrp]: "1750",
    }),
    row({
      [cModel]: "ER160",
      [cAh]: "120",
      [cWarranty]: "18M",
      [cLoader]: "24–26 AH",
      [cBackup]: "2H",
      [cWeight]: "34–35",
      [cDp]: "6650",
      [cMrp]: "8500",
    }),
    row({
      [cModel]: "ER180",
      [cAh]: "140",
      [cWarranty]: "24M",
      [cLoader]: "28–30 AH",
      [cBackup]: "2H 30M",
      [cWeight]: "40–42",
      [cDp]: "7250",
      [cMrp]: "10500",
    }),
    row({
      [cModel]: "HT 1800",
      [cAh]: "150",
      [cWarranty]: "24M",
      [cLoader]: "30–32 AH",
      [cBackup]: "3H 15M",
      [cWeight]: "48–50",
      [cDp]: "8000",
      [cMrp]: "12500",
    }),
    row({
      [cModel]: "HT 2200",
      [cAh]: "180",
      [cWarranty]: "30M",
      [cLoader]: "34–36 AH",
      [cBackup]: "3H 45M",
      [cWeight]: "53–55",
      [cDp]: "10500",
      [cMrp]: "15550",
    }),
    row({
      [cModel]: "HT 2400",
      [cAh]: "220",
      [cWarranty]: "36M",
      [cLoader]: "38–40 AH",
      [cBackup]: "4H 15M",
      [cWeight]: "60–62",
      [cDp]: "12000",
      [cMrp]: "18550",
    }),
  ];

  const schemes = [
    {
      id: uuidv4(),
      body: "20 Battery on — Free 1 Battery (JT 1800 — ₹12400).",
    },
    {
      id: uuidv4(),
      body: "150 Battery on — Free 1 Activa (Ex-Showroom Price).",
    },
    {
      id: uuidv4(),
      body: "200 Battery on — Free 1 EV Chetak (Ex-Showroom Price).",
    },
  ];

  return {
    meta: {
      businessName: "Ashish Inverter Battery House",
      website: "https://www.ashishinverterbatteryhouse.in/",
      tagline:
        "Trusted power-backup partner — quality products and fair pricing for authorised dealers.",
      intro: "",
      footerNote: "",
      footnoteDisclaimer: DEFAULT_FOOTNOTE_DISCLAIMER,
      schemeDisclaimer:
        "Scheme available for JT Battery for 6 months. Terms apply.",
    },
    logoDataUrl: null,
    columns,
    rows,
    schemes,
  };
}

function normalizeCatalog(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (!Array.isArray(raw.columns) || !Array.isArray(raw.rows)) return null;
  const validCols = raw.columns.filter(
    (c) => c && typeof c.id === "string" && typeof c.label === "string"
  );
  if (validCols.length === 0) return null;
  const colIds = new Set(validCols.map((c) => c.id));
  const rows = raw.rows.map((r) => {
    const id = typeof r?.id === "string" ? r.id : uuidv4();
    const cells = {};
    const src = r?.cells && typeof r.cells === "object" ? r.cells : {};
    colIds.forEach((cid) => {
      cells[cid] = src[cid] != null ? String(src[cid]) : "";
    });
    return { id, cells };
  });
  const schemes = Array.isArray(raw.schemes)
    ? raw.schemes.map((s) => ({
        id: typeof s?.id === "string" ? s.id : uuidv4(),
        body: s?.body != null ? String(s.body) : "",
      }))
    : [];
  const meta = {
    businessName:
      raw.meta?.businessName != null
        ? String(raw.meta.businessName)
        : "Ashish Inverter Battery House",
    website:
      raw.meta?.website != null ? String(raw.meta.website) : "",
    tagline:
      raw.meta?.tagline != null ? String(raw.meta.tagline) : "",
    intro: raw.meta?.intro != null ? String(raw.meta.intro) : "",
    footerNote:
      raw.meta?.footerNote != null ? String(raw.meta.footerNote) : "",
    footnoteDisclaimer:
      raw.meta?.footnoteDisclaimer !== undefined &&
      raw.meta?.footnoteDisclaimer !== null
        ? String(raw.meta.footnoteDisclaimer)
        : DEFAULT_FOOTNOTE_DISCLAIMER,
    schemeDisclaimer:
      raw.meta?.schemeDisclaimer != null
        ? String(raw.meta.schemeDisclaimer)
        : "",
  };
  const logoDataUrl =
    typeof raw.logoDataUrl === "string" ? raw.logoDataUrl : null;
  return {
    meta,
    logoDataUrl,
    columns: validCols,
    rows,
    schemes,
  };
}

export function loadCatalog() {
  try {
    const s = localStorage.getItem(CATALOG_STORAGE_KEY);
    if (!s) return createDefaultCatalog();
    const parsed = JSON.parse(s);
    const normalized = normalizeCatalog(parsed);
    return normalized || createDefaultCatalog();
  } catch {
    return createDefaultCatalog();
  }
}

export function saveCatalog(catalog) {
  try {
    localStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify(catalog));
  } catch {
    /* quota or private mode */
  }
}

export function addColumn(catalog) {
  const id = `c_${uuidv4().slice(0, 8)}`;
  const label = "New field";
  const columns = [...catalog.columns, { id, label }];
  const rows = catalog.rows.map((r) => ({
    ...r,
    cells: { ...r.cells, [id]: "" },
  }));
  return { ...catalog, columns, rows };
}

export function removeColumn(catalog, columnId) {
  if (catalog.columns.length <= 1) return catalog;
  const columns = catalog.columns.filter((c) => c.id !== columnId);
  const rows = catalog.rows.map((r) => {
    const cells = { ...r.cells };
    delete cells[columnId];
    return { ...r, cells };
  });
  return { ...catalog, columns, rows };
}

export function updateColumnLabel(catalog, columnId, label) {
  const columns = catalog.columns.map((c) =>
    c.id === columnId ? { ...c, label } : c
  );
  return { ...catalog, columns };
}

export function addRow(catalog) {
  const cells = {};
  catalog.columns.forEach((c) => {
    cells[c.id] = "";
  });
  return {
    ...catalog,
    rows: [...catalog.rows, { id: uuidv4(), cells }],
  };
}

export function updateCell(catalog, rowId, columnId, value) {
  const rows = catalog.rows.map((r) =>
    r.id === rowId
      ? { ...r, cells: { ...r.cells, [columnId]: value } }
      : r
  );
  return { ...catalog, rows };
}

export function removeRow(catalog, rowId) {
  return {
    ...catalog,
    rows: catalog.rows.filter((r) => r.id !== rowId),
  };
}

export function addSchemeLine(catalog) {
  return {
    ...catalog,
    schemes: [...catalog.schemes, { id: uuidv4(), body: "" }],
  };
}

export function updateSchemeLine(catalog, schemeId, body) {
  const schemes = catalog.schemes.map((s) =>
    s.id === schemeId ? { ...s, body } : s
  );
  return { ...catalog, schemes };
}

export function removeSchemeLine(catalog, schemeId) {
  return {
    ...catalog,
    schemes: catalog.schemes.filter((s) => s.id !== schemeId),
  };
}

export function updateMeta(catalog, patch) {
  return {
    ...catalog,
    meta: { ...catalog.meta, ...patch },
  };
}

export function setLogoDataUrl(catalog, dataUrl) {
  return { ...catalog, logoDataUrl: dataUrl };
}

/** Read file as data URL; returns { ok, dataUrl?, error? } */
export function readLogoFile(file) {
  if (!file || !file.type.startsWith("image/")) {
    return { ok: false, error: "Please choose an image file." };
  }
  if (file.size > MAX_LOGO_BYTES) {
    return {
      ok: false,
      error: `Image must be under ${Math.round(MAX_LOGO_BYTES / 1024)} KB for browser storage.`,
    };
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl === "string") {
        resolve({ ok: true, dataUrl });
      } else {
        resolve({ ok: false, error: "Could not read file." });
      }
    };
    reader.onerror = () =>
      resolve({ ok: false, error: "Could not read file." });
    reader.readAsDataURL(file);
  });
}
